import uiConfig from 'src/lib/ui/config';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import { createEmitter, createPersistentEmitter } from 'src/lib/Emitter';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { shouldShowFooter } from 'src/redux/getters/ui';
import animate from '@play-co/timestep-core/lib/animate';
import { animDuration, applyRelativeOffset, getScreenTop } from 'src/lib/utils';
import { getCareActionLocked } from '../care/care.utils';
import { getFriendsMenuLocked } from '../visit/visit.util';
import { FooterCareButton } from './footer/FooterCareButton';
import { FooterFriendsButton } from './footer/FooterFriendsButton';
import { FooterMenuButton } from './footer/FooterMenuButton';
import { Match2MinigameButton } from './footer/Match2MinigameButton';
import { ShopMenuButton } from './footer/ShopMenuButton';
import { getMenuLocked, getShopLocked } from './footer/footer.utils';
import { registerHandTarget } from '../tutorial/services/TutorialHandService';
import { getMatch2Locked } from '../minigame/match2-timestep/match2.play';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import { user } from 'src/StateObserver';
import ab from 'src/replicant/ruleset/ab';

export default class Footer extends FlexBoxView {
  public readonly menuButton: FooterMenuButton;
  public readonly careButton: FooterCareButton;
  public readonly minigameButton: Match2MinigameButton;

  constructor({ superview }: Pick<ViewOpts, 'superview'>) {
    super({
      superview,
      ...uiConfig.footer.container,
    });

    this.menuButton = this.addSubview(new FooterMenuButton());

    const friendButton = this.addSubview(new FooterFriendsButton());

    friendButton.updateOpts({
      zIndex: -1, // Be behind of the minigame button
    });
    registerHandTarget('friendsButton', friendButton);

    this.minigameButton = this.addSubview(new Match2MinigameButton());
    registerHandTarget('minigameButton', this.minigameButton);

    const careButton = this.addSubview(new FooterCareButton());
    careButton.updateOpts({
      zIndex: -1, // Be behind of the minigame button
    });
    this.careButton = careButton;
    registerHandTarget('careButton', this.careButton);

    //Coins energy shop
    const shopButton = this.addSubview(new ShopMenuButton());

    // anchor elements
    createEmitter(this, ({ ui }) => ui.screenSize).addListener((screen) => {
      this.updateOpts({
        y: getScreenTop() + screen.height - this.style.height,
      });
    });

    // Disable care/minigame buttons while asleep, or if the player hasn't unlocked them.
    createEmitter(this, () => ({
      menu: getMenuLocked(),
      shop: getShopLocked(),
      careAction: getCareActionLocked(),
      minigame: getMatch2Locked(),
      friends: getFriendsMenuLocked(),
    })).addListener(({ menu, shop, careAction, minigame, friends }) => {
      // Lock menu button if needed.
      if (menu.locked) {
        this.menuButton.setActive(false);
      }
      this.menuButton.setLocked(menu.locked, menu.reason);

      // Lock shop button if needed.
      if (shop.locked) {
        shopButton.setActive(false);
      }
      shopButton.setLocked(shop.locked, shop.reason);

      // Lock care button if needed.
      if (careAction.locked) {
        careButton?.setActive(false);
      }
      careButton?.setLocked(careAction.locked, careAction.reason);

      // Lock friends button if needed.
      if (friends.locked) {
        friendButton.setActive(false);
      }
      friendButton.setLocked(friends.locked, friends.reason);

      // Lock minigames button if needed.
      this.minigameButton.setLocked(minigame.locked, minigame.reason);
    });

    createPersistentEmitter(shouldShowFooter).addListener((showFooter) => {
      if (showFooter) {
        this.fadeIn();
      } else {
        this.fadeOut();
      }
    });
  }

  private fadeIn() {
    animate(this)
      .clear()
      .wait(animDuration * 4)
      .now(() =>
        this.updateOpts({
          visible: true,
          opacity: this.style.opacity,
          offsetY: 100,
        }),
      )
      .then(
        { opacity: this.style.opacity, offsetY: 100 },
        animDuration,
        animate.easeOut,
      )
      .then({ opacity: 1, offsetY: 0 }, animDuration, animate.easeOut);
  }

  private fadeOut() {
    animate(this)
      .clear()
      .wait(0)
      .now(
        { opacity: this.style.opacity, offsetY: this.style.offsetY },
        0,
        animate.easeOut,
      )
      .then(
        { opacity: this.style.opacity, offsetY: this.style.offsetY },
        animDuration * 0.5,
        animate.easeOut,
      )
      .then({ opacity: 0, offsetY: 100 }, animDuration, animate.easeOut)
      .then(() => {
        this.style.visible = false;
      });
  }

  public getMatch2PlayIconPos(relativeTo?: View): { x: number; y: number } {
    const position = this.minigameButton.getPosition(this);
    if (relativeTo) applyRelativeOffset(position, this, relativeTo);
    return {
      x: position.x + this.minigameButton.style.width / 2,
      y: position.y + this.minigameButton.style.height / 2,
    };
  }
}
